import React, { useState, useCallback, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import JsonView from "@uiw/react-json-view";
import { useAppSelector } from "./store/hooks";
import {
    selectSelectedTable,
    selectSelectedTableColumnNames,
    selectSelectedTableData,
    selectSelectedTableDataNumRows,
    selectTableDataIsAvailable,
} from "./store/reducers/mdUploadSlice";

export default function MDToolTableViewer() {
    const selectedTable = useAppSelector(selectSelectedTable);
    const selectedTableData = useAppSelector(selectSelectedTableData);
    const selectedTableDataNumRows = useAppSelector(selectSelectedTableDataNumRows);
    const selectedTableColumnNames = useAppSelector(selectSelectedTableColumnNames);

    const [maxWidth, setMaxWidth] = useState("100%");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Reset the pagination to 0 when the table gets changed
    useEffect(() => {
        setPage(0);
    }, [selectedTable]);

    // Adjust the inner width of each table based off of the contents inside
    useEffect(() => {
        const handleResize = () => {
            // Calculate the width of the inner table based off of the sum of the lengths of each column header
            const computedMaxWidth = selectedTableColumnNames.reduce((sum, key) => sum + key.length, 0) * 35;
            const widthRatio = computedMaxWidth / window.innerWidth;
            setMaxWidth(widthRatio < 1 ? "100%" : `${widthRatio * 100}%`);
        };

        // Call handler once to set the initial width on mount
        handleResize();

        // Add event listener to window to handle resizing
        window.addEventListener("resize", handleResize);

        // Remove event listener on unmount
        return () => window.removeEventListener("resize", handleResize);
    }, [selectedTableColumnNames]);

    const onPageChange = useCallback(
        (_: unknown, newPage: number) => {
            setPage(newPage);
        },
        [setPage]
    );

    const onRowsPerPageChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setRowsPerPage(+event.target.value);
            setPage(0);
        },
        [setRowsPerPage, setPage]
    );

    if (!selectedTable || !selectedTableData || !selectedTableDataNumRows || !selectedTableColumnNames) {
        return (
            <Typography
                variant="body1"
                sx={{
                    textAlign: "center",
                    marginTop: 4,
                    fontSize: "20px",
                    fontStyle: "italic",
                }}
            >
                No data available.
            </Typography>
        );
    }
    return (
        <>
            <TableContainer
                component={Paper}
                sx={{
                    marginTop: 3,
                    width: "100%",
                    overflowX: "auto",
                }}
            >
                <Table
                    sx={{
                        width: maxWidth,
                        tableLayout: "fixed",
                    }}
                    aria-label="simple table"
                >
                    <TableHead>
                        <TableRow>
                            {selectedTableColumnNames.map((column) => {
                                return (
                                    <TableCell
                                        key={column}
                                        sx={{
                                            minWidth: "150px",
                                            padding: 2,
                                            whitespace: "nowrap",
                                        }}
                                    >
                                        {column}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {selectedTableData
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, index) => (
                                <TableRow key={index}>
                                    {Object.keys(row).map((key, subIndex) => {
                                        return (
                                            <TableCell
                                                key={subIndex}
                                                sx={{
                                                    minWidth: "150px",
                                                    padding: 2,
                                                    whitespace: "nowrap",
                                                }}
                                            >
                                                {(row[key]?.constructor === Object && <JsonView value={row[key]} collapsed={1} />) ||
                                                    (typeof row[key] === "boolean" && row[key].toString()) ||
                                                    row[key]}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={selectedTableDataNumRows}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
            />
        </>
    );
}
